// @ts-check
import { parse, differenceInMinutes, startOfDay, addMinutes, addDays } from 'date-fns';
import { buildVisitScheduleItem } from './buildVisitScheduleItem';
import { buildPadPrescriptionEvent } from './buildPadPrescriptionEvent';
import { SHIFT_START_MINUTES } from '../constants/shiftConstants';

/** @typedef {number|null} MaxTimeDifferenceMinutes  */

/**
 * Agrupa eventos com base em uma janela de tempo máxima e limites de turno.
 * Eventos só são agrupados se estiverem dentro da janela de tempo E no mesmo turno.
 *
 * @param {import('./buildPadPrescriptionEvent').DtEvent[]} dtEvents - Lista de eventos (DtEvent) a serem agrupados.
 * @param {MaxTimeDifferenceMinutes} [maxTimeDifferenceMinutes=60] - Diferença máxima em minutos para agrupamento.
 * @returns {Map<string, any>} Um Map contendo as visitas agrupadas.
 */
export function applyGroupEventsByTimeGapRules(dtEvents, maxTimeDifferenceMinutes) {
  // Validação rápida da entrada
  if (!Array.isArray(dtEvents) || dtEvents.length === 0) {
    return new Map();
  }

  if (!maxTimeDifferenceMinutes) {
    maxTimeDifferenceMinutes = 60;
  }

  // 1. Ordena os eventos cronologicamente para que a busca por proximidade funcione
  const dtEventSortedList = [...dtEvents].sort((a, b) => toDate(a).getTime() - toDate(b).getTime());

  const scheduleMap = new Map();

  // Array auxiliar para sabermos quais chaves/visitas já criamos e comparar os tempos
  const createdVisitsTimeline = [];

  // 2. Percorre os eventos ordenados
  // eslint-disable-next-line no-unused-vars
  for (const currentDtEvent of dtEventSortedList) {
    const { date, time, padPrescriptionId /* , padPrescription */ } = currentDtEvent;
    const currentEventDate = toDate(currentDtEvent);

    let matchedVisitKey = null;

    // Verifica se este evento se encaixa em alguma visita já criada na timeline
    // Como está ordenado, geralmente fará sentido comparar com os últimos criados
    for (let i = createdVisitsTimeline.length - 1; i >= 0; i--) {
      const existingVisit = createdVisitsTimeline[i];
      const diff = Math.abs(differenceInMinutes(currentEventDate, existingVisit.dateTime));

      // Usa getShiftBoundaries para verificar se ambos eventos estão no mesmo turno
      const currentBoundaries = getShiftBoundaries(currentEventDate, maxTimeDifferenceMinutes);
      const existingBoundaries = getShiftBoundaries(
        existingVisit.dateTime,
        maxTimeDifferenceMinutes
      );
      const sameShift =
        currentBoundaries.shiftStart.getTime() === existingBoundaries.shiftStart.getTime() &&
        currentBoundaries.shiftEnd.getTime() === existingBoundaries.shiftEnd.getTime();

      if (diff <= maxTimeDifferenceMinutes && sameShift) {
        matchedVisitKey = existingVisit.uniqueVisitKey;
        break; // Encontrou a visita correspondente dentro da janela de tempo e mesmo turno
      }
    }

    if (matchedVisitKey) {
      // Se encontrou uma visita próxima, apenas adiciona o ID do medicamento a ela
      const visit = scheduleMap.get(matchedVisitKey);
      visit.padPrescriptionIds.push(padPrescriptionId);

      const nEvent = buildPadPrescriptionEvent(currentDtEvent);
      visit.padPrescriptionEvents.push(nEvent);
    } else {
      // Se não encontrou nenhuma visita no limite de tempo, cria uma NOVA visita
      const uniqueVisitKey = `${date.replace(/\//g, '-')}-${time.replace(/:/g, '')}`;
      const newItem = buildVisitScheduleItem(currentDtEvent);

      scheduleMap.set(uniqueVisitKey, {
        ...newItem,
        uniqueVisitKey
      });

      // Salva na timeline para comparar com os próximos eventos do loop
      createdVisitsTimeline.push({
        uniqueVisitKey,
        dateTime: currentEventDate
      });
    }
  }

  return scheduleMap;
}

/**
 * Retorna a data/hora de 07:00 do dia do evento (ou dia anterior se antes das 07:00).
 * Este é o início do "ciclo de turnos" para aquela data.
 *
 * @param {Date} dateTime - Data/hora do evento
 * @returns {Date} 07:00 do dia do evento (ajustado se antes das 07:00)
 */
function getShiftCycleStart(dateTime) {
  const dayStart = startOfDay(dateTime);
  const shiftStart = addMinutes(dayStart, SHIFT_START_MINUTES);

  // Se o evento é antes das 07:00, pertence ao ciclo que começou no dia anterior às 07:00
  if (dateTime < shiftStart) {
    return addDays(shiftStart, -1);
  }
  return shiftStart;
}

/**
 * Retorna os limites (início e fim) do turno para um dateTime específico.
 * Turnos são blocos de maxTimeDifferenceMinutes iniciando às 07:00.
 * Turno 0: 07:00 até 07:00 + maxTimeDifferenceMinutes
 * Turno 1: 07:00 + maxTimeDifferenceMinutes até 07:00 + 2*maxTimeDifferenceMinutes
 * etc.
 *
 * @param {Date} dateTime - Data/hora de referência
 * @param {number} maxTimeDifferenceMinutes - Duração do turno em minutos (ex: 720 para 12h)
 * @returns {{ shiftStart: Date, shiftEnd: Date }} Objeto com início e fim do turno
 */
export function getShiftBoundaries(dateTime, maxTimeDifferenceMinutes) {
  const cycleStart = getShiftCycleStart(dateTime);
  const minutesSinceCycleStart = differenceInMinutes(dateTime, cycleStart);
  const shiftIndex = Math.floor(minutesSinceCycleStart / maxTimeDifferenceMinutes);

  const actualShiftStart = addMinutes(cycleStart, shiftIndex * maxTimeDifferenceMinutes);
  const actualShiftEnd = addMinutes(actualShiftStart, maxTimeDifferenceMinutes);
  return { shiftStart: actualShiftStart, shiftEnd: actualShiftEnd };
}

/**
 * Converte um objeto com data e hora string para um objeto Date.
 *
 * @param {Object} param0 - O objeto contendo a data e a hora.
 * @param {string} param0.date - A data no formato "DD/MM/YYYY" (ex: "22/06/2026").
 * @param {string} param0.time - A hora no formato "HH:mm" ou "HH:mm:ss" (ex: "14:30").
 * @throws {Error} Se a string gerada não for uma data/hora válida.
 * @returns {Date} Um objeto Date válido.
 */
const toDate = ({ date, time }) => {
  const [day, month, year] = date.trim().split('/');
  const isoString = `${year}-${month}-${day}T${time.trim()}:00`;
  const parsed = parse(isoString);

  if (isNaN(parsed.getTime())) {
    throw new Error(`Data/hora inválida: ${date} ${time}`);
  }
  return parsed;
};
